import React from 'react';
import { RotateCcw, MessageSquare } from 'lucide-react';

import { Card, TextButton, Heading, Button } from '../../shared/ui';

import { DocumentActions } from './document-actions';

import { DemoBanner } from './DemoBanner';

type PrepHighlightsProps = {
  clientName?: string;
};

export function PrepHighlights(_props: PrepHighlightsProps) {
  return (
    <Card radius="control">
      <DemoBanner section="Подготовка" />
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-surface-muted">
        <Heading size="lg" as="h3">
          Подготовка к сессии
        </Heading>
        <DocumentActions />
      </div>

      <div className="bg-report-surface border border-report-line rounded-control p-4 mb-6">
        <p className="text-ui text-ink-muted">
          <span className="text-report font-medium">Примечание:</span> Этот отчёт обновляется с каждой новой сессией.
          Любые ваши правки заблокируют дальнейшие обновления, пока вы не{' '}
          <TextButton tone="report" underline="always" className="gap-1" startIcon={<RotateCcw size={12} />}>
            сбросите
          </TextButton>{' '}
          их.
        </p>
      </div>

      <section className="mb-6">
        <Heading size="sm" as="h4" className="mb-2">
          Запрос
        </Heading>
        <p className="text-ui text-ink-muted">
          Управление устойчивыми чувствами вины и тревоги, улучшение самоотношения
        </p>
      </section>

      <section className="mb-6">
        <Heading size="sm" as="h4" className="mb-3">
          План действий клиента
        </Heading>
        <ul className="space-y-2 text-ui text-ink-muted">
          {[
            'Практиковать осознавание и называние вины в момент её возникновения;',
            'Использовать технику визуализации для экстернализации вины;',
            'Применять самосострадательные послания при ощущении вины;',
            'Обращаться к доверенным людям в сложные моменты;',
            'Практиковать экспрессивное письмо или рисование для переработки чувств;',
            'Говорить с собой словами мягкости и понимания.',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-ink-subtle mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <TextButton
          tone="report"
          underline="none"
          className="mt-3 gap-2 text-left text-ui"
          startIcon={<MessageSquare size={14} />}
        >
          Хотите обсудить, как поддержать эти цели? Спросите ИИ-ассистента
        </TextButton>
      </section>

      <section className="mb-6">
        <Heading size="sm" as="h4" className="mb-2">
          Краткое резюме последней сессии
        </Heading>
        <p className="text-ui text-ink-muted mb-3">
          Клиент обсудил прогресс в реализации стратегий самопомощи и решении ситуации с проблемным сотрудником.
          Выразила смесь гордости и вины за установление границ на работе — опасалась, что была слишком жёсткой.
        </p>
        <p className="text-ui text-ink-muted">
          Сессия была посвящена исследованию глубинной вины клиента, которая проявляется в разных сферах жизни — работе,
          отношениях и самопомощи. Терапевт провёл телесно-ориентированную технику визуализации для работы с виной.
        </p>
      </section>

      <section>
        <Heading size="sm" as="h4" className="mb-2">
          Рекомендации к следующей сессии
        </Heading>
        <ul className="space-y-2 text-ui text-ink-muted">
          <li className="flex items-start gap-2">
            <span className="text-ink-subtle">•</span>
            <span>Проверить выполнение домашнего задания по дневнику вины</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-ink-subtle">•</span>
            <span>Исследовать связь между перфекционизмом и страхом отвержения</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-ink-subtle">•</span>
            <span>Продолжить работу с техникой самосострадания</span>
          </li>
        </ul>
      </section>
    </Card>
  );
}
